import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, newIdempotencyKey } from "@/api/client";
import { X, Truck, CheckCircle, AlertCircle, Loader2, ExternalLink, Package, Scale } from "lucide-react";

export default function ShippingLabelsModal({ mailing, onClose, onComplete }) {
  const navigate = useNavigate();

  // One key for the life of this modal. Every retry of the purchase reuses it,
  // which is what makes a timeout safe to retry.
  const idempotencyKey = useRef(newIdempotencyKey());
  const override = mailing.shipping_override;
  const isOverrideActive = override?.enabled && override?.address_line1 && override?.city && override?.state && override?.zip;
  const [step, setStep] = useState("enter_weights");
  const [boxWeights, setBoxWeights] = useState({});
  const [boxRates, setBoxRates] = useState([]);
  const [selectedRates, setSelectedRates] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [carrierNotices, setCarrierNotices] = useState([]);

  useEffect(() => {
    if (mailing.box_packing?.boxes) {
      const initialWeights = {};
      mailing.box_packing.boxes.forEach(box => {
        initialWeights[box.container_number] = box.weight ? box.weight.toFixed(1) : "10";
      });
      setBoxWeights(initialWeights);
    }
  }, [mailing.id]);

  const handleContinueWithWeights = () => {
    const allWeightsEntered = mailing.box_packing.boxes.every(box => {
      const weight = parseFloat(boxWeights[box.container_number]);
      return !isNaN(weight) && weight > 0;
    });

    if (!allWeightsEntered) {
      setError("Please enter a valid weight for all boxes");
      return;
    }

    setError("");
    setStep("loading");
    fetchRates();
  };

  const fetchRates = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.shipping.rates(mailing.id, boxWeights);

      // Why a carrier is missing. Shippo returns rates for whatever accounts
      // are active and explains the rest in the shipment messages, so without
      // this a UPS account that is inactive or rejecting the address looks
      // exactly like one that was never connected.
      setCarrierNotices(response.carrier_notices ?? []);

      if (response.box_rates && response.box_rates.length > 0) {
        setBoxRates(response.box_rates);

        // The server sorts rates cheapest-first, so index 0 is the cheapest
        // rather than whichever Shippo happened to return first — which is what
        // the old default selected, and what "apply to all" then propagated.
        const autoSelected = {};
        response.box_rates.forEach(boxRate => {
          if (boxRate.rates && boxRate.rates.length > 0) {
            autoSelected[boxRate.box_number] = boxRate.rates[0].object_id;
          }
        });
        setSelectedRates(autoSelected);

        setStep("select_rates");
      } else {
        setError(response.errors?.[0]?.error || 'No rates available');
        setStep("select_rates");
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to fetch shipping rates");
      setStep("enter_weights");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyToAll = (provider, service) => {
    const newSelectedRates = {};
    boxRates.forEach(boxRate => {
      const matchingRate = boxRate.rates?.find(r => r.provider === provider && r.service === service);
      if (matchingRate) {
        newSelectedRates[boxRate.box_number] = matchingRate.object_id;
      }
    });
    setSelectedRates(newSelectedRates);
  };

  const handleCreateLabels = async () => {
    const rateSelections = Object.entries(selectedRates).map(([boxNum, rateId]) => {
      const boxRate = boxRates.find(br => br.box_number === parseInt(boxNum));
      const rate = boxRate?.rates.find(r => r.object_id === rateId);
      
      return {
        box_number: parseInt(boxNum),
        rate_id: rateId,
        rate_amount: rate?.amount || '0',
        rate_provider: rate?.provider || 'Unknown',
        rate_service: rate?.service || 'Unknown'
      };
    });

    if (rateSelections.length !== mailing.box_packing.boxes.length) {
      setError("Please select a shipping option for each box");
      return;
    }

    setLoading(true);
    setError("");
    setStep("creating");

    try {
        // The key is held in a ref for the life of this modal, so a retry after
        // a timeout replays the same purchase instead of buying a second set of
        // labels. Regenerating it here would defeat the whole mechanism.
        const response = await api.shipping.purchase(mailing.id, {
          idempotencyKey: idempotencyKey.current,
          boxWeights,
          selections: rateSelections,
        });

        setResult(response);
        setStep("results");

        if (response.summary.failed > 0) {
          setError(
            `${response.summary.failed} of ${response.summary.total} label(s) could not be bought. ` +
            `The rest were purchased.`
          );
        }

        if (onComplete) {
          onComplete(mailing.id, mailing.mailing_name, mailing.order_number, false);
        }
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to create shipping labels");
        setStep("select_rates");
      } finally {
        setLoading(false);
      }
  };

  const getSelectedRate = (boxNumber) => {
    const boxRate = boxRates.find(br => br.box_number === boxNumber);
    const rateId = selectedRates[boxNumber];
    return boxRate?.rates.find(r => r.object_id === rateId);
  };

  const getTotalCost = () => {
    return Object.entries(selectedRates).reduce((sum, [boxNum, rateId]) => {
      const rate = getSelectedRate(parseInt(boxNum));
      return sum + (rate ? parseFloat(rate.amount) : 0);
    }, 0).toFixed(2);
  };

  const getTotalWeight = () => {
    return Object.values(boxWeights).reduce((sum, weight) => {
      return sum + (parseFloat(weight) || 0);
    }, 0).toFixed(1);
  };

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      padding: "1rem",
    }}>
      <div className="card" style={{ maxWidth: "900px", width: "100%", maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <div>
            <h3 style={{ fontSize: "1.5rem", fontWeight: 600, color: "#1e293b" }}>
              Create Shipping Labels
            </h3>
            <p style={{ color: "#64748b", fontSize: "0.875rem", marginTop: "0.25rem" }}>
              {mailing.mailing_name}
            </p>
            {isOverrideActive && (
              <div style={{ marginTop: "0.5rem", padding: "0.5rem 0.75rem", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "6px", fontSize: "0.8rem", color: "#dc2626", fontWeight: 600 }}>
                ⚠️ Override Active — All labels ship to: {override.customer_name || 'Customer'}, {override.address_line1}, {override.city}, {override.state} {override.zip}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "0.5rem",
              color: "#64748b",
            }}
          >
            <X size={24} />
          </button>
        </div>

        {error && <div className="error">{error}</div>}
        {carrierNotices.length > 0 && (
          <div className="alert alert-warning">
            <strong>Not every carrier quoted this shipment.</strong>
            <ul style={{ margin: "0.5rem 0 0", paddingLeft: "1.25rem" }}>
              {carrierNotices.map((notice, index) => (
                <li key={index}>{notice}</li>
              ))}
            </ul>
          </div>
        )}

        {step === "enter_weights" && mailing.box_packing?.boxes && (
          <div>
            <div style={{ marginBottom: "1.5rem" }}>
              <h4 style={{ fontSize: "1rem", fontWeight: 600, color: "#1e293b", marginBottom: "0.5rem" }}>
                Enter Weight for Each Box
              </h4>
              <p style={{ fontSize: "0.875rem", color: "#64748b" }}>
                Provide the weight in pounds for each box to get accurate shipping rates
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginBottom: "1.5rem" }}>
              {mailing.box_packing.boxes.map((box) => (
                <div 
                  key={box.container_number}
                  style={{
                    background: "#f8fafc",
                    border: "2px solid #e2e8f0",
                    borderRadius: "12px",
                    padding: "1.5rem"
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                    <div>
                      <div style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1e293b" }}>
                        Box #{box.container_number} - {box.container_size ? box.container_size.name : box.container_type}
                      </div>
                      <div style={{ fontSize: "0.875rem", color: "#64748b", marginTop: "0.25rem" }}>
                        {box.bundles} bundle{box.bundles !== 1 ? 's' : ''} • {box.pieces.toLocaleString()} pieces
                      </div>
                      <div style={{ fontSize: "0.875rem", color: isOverrideActive ? "#dc2626" : "#3b82f6", marginTop: "0.25rem" }}>
                        → {isOverrideActive ? `${override.customer_name || 'Customer'} (Override)` : box.post_office_name || 'Destination not set'}
                      </div>
                      {box.container_size && (
                        <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: "0.25rem" }}>
                          Size: {box.container_size.length}" × {box.container_size.width}" × {box.container_size.height}"
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                      <Scale size={24} style={{ color: "#64748b" }} />
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={boxWeights[box.container_number] || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^\d*\.?\d*$/.test(val)) {
                              setBoxWeights({ ...boxWeights, [box.container_number]: val });
                            }
                          }}
                          onWheel={(e) => e.currentTarget.blur()}
                          placeholder="0.0"
                          style={{
                            width: "100px",
                            padding: "0.75rem",
                            border: "2px solid #e2e8f0",
                            borderRadius: "8px",
                            fontSize: "1.125rem",
                            fontWeight: 600,
                            textAlign: "center",
                            MozAppearance: "textfield",
                            appearance: "textfield"
                          }}
                        />
                        <span style={{ fontSize: "1rem", fontWeight: 600, color: "#64748b" }}>lbs</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ fontSize: "0.75rem", color: "#64748b", paddingTop: "0.75rem", borderTop: "1px solid #e2e8f0" }}>
                    <strong>Routes:</strong> {box.routes.map(r => r.route || r).join(", ")}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ 
              background: "#f0f9ff", 
              border: "2px solid #bfdbfe",
              padding: "1rem", 
              borderRadius: "8px", 
              marginBottom: "1.5rem" 
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: "0.875rem", color: "#1e40af", marginBottom: "0.25rem" }}>
                    Total Weight
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "#3b82f6" }}>
                    {mailing.box_packing?.total_boxes || 0} boxes
                  </div>
                </div>
                <div style={{ fontSize: "2rem", fontWeight: 700, color: "#2563eb" }}>
                  {getTotalWeight()} lbs
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
              <button className="btn btn-outline" onClick={onClose}>
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleContinueWithWeights}
                disabled={Object.keys(boxWeights).length !== mailing.box_packing?.total_boxes || Object.values(boxWeights).some(w => !w || parseFloat(w) <= 0)}
              >
                <Truck size={20} />
                Continue to Get Rates
              </button>
            </div>
          </div>
        )}

        {step === "loading" && (
          <div style={{ textAlign: "center", padding: "3rem" }}>
            <Loader2 size={48} style={{ color: "#3b82f6", animation: "spin 1s linear infinite", margin: "0 auto 1rem" }} />
            <p style={{ color: "#64748b" }}>Fetching shipping rates...</p>
          </div>
        )}

        {step === "select_rates" && (
          <div>
            <div style={{ marginBottom: "1.5rem" }}>
              <h4 style={{ fontSize: "1rem", fontWeight: 600, color: "#1e293b", marginBottom: "0.5rem" }}>
                Select Shipping Options ({boxRates.length} boxes)
              </h4>
              <p style={{ fontSize: "0.875rem", color: "#64748b" }}>
                Choose a carrier and service level for each box, or apply one option to all boxes
              </p>
            </div>

            {boxRates.length > 1 && boxRates[0]?.rates && boxRates[0].rates.length > 0 && (
              <div style={{
                background: "#fef9c3",
                border: "2px solid #fbbf24",
                borderRadius: "12px",
                padding: "1rem",
                marginBottom: "1.5rem"
              }}>
                <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "#92400e", marginBottom: "0.75rem" }}>
                  ⚡ Quick Apply to All Boxes
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                  {boxRates[0].rates.map((rate) => (
                    <button
                      key={rate.object_id}
                      onClick={() => handleApplyToAll(rate.provider, rate.service)}
                      className="btn btn-sm"
                      style={{
                        background: "white",
                        border: "1px solid #d97706",
                        color: "#92400e",
                        fontSize: "0.75rem",
                        padding: "0.5rem 0.75rem",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {rate.provider} - {rate.service}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: "0.7rem", color: "#92400e", marginTop: "0.5rem", opacity: 0.8 }}>
                  Click to apply this option to all boxes (where available)
                </p>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginBottom: "1.5rem" }}>
              {boxRates.map((boxRate) => (
                <div 
                  key={boxRate.box_number}
                  style={{
                    background: "#f8fafc",
                    border: "2px solid #e2e8f0",
                    borderRadius: "12px",
                    padding: "1.5rem"
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1rem" }}>
                    <div>
                      <div style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1e293b" }}>
                        Box #{boxRate.box_number}
                      </div>
                      <div style={{ fontSize: "0.875rem", color: "#64748b", marginTop: "0.25rem" }}>
                        Destination: {boxRate.post_office} • Weight: {boxRate.weight || 'N/A'} lbs
                        {boxRate.dimensions && (
                          <span> • Size: {boxRate.dimensions.length}" × {boxRate.dimensions.width}" × {boxRate.dimensions.height}"</span>
                        )}
                      </div>
                    </div>
                    {selectedRates[boxRate.box_number] && (
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#10b981" }}>
                          ${getSelectedRate(boxRate.box_number)?.amount}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                          {getSelectedRate(boxRate.box_number)?.estimated_days} days
                        </div>
                      </div>
                    )}
                  </div>

                  {boxRate.rates && boxRate.rates.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      {boxRate.rates.map((rate) => (
                        <label
                          key={rate.object_id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "1rem",
                            background: selectedRates[boxRate.box_number] === rate.object_id ? "#dbeafe" : "white",
                            border: selectedRates[boxRate.box_number] === rate.object_id ? "2px solid #3b82f6" : "1px solid #e2e8f0",
                            borderRadius: "8px",
                            cursor: "pointer",
                            transition: "all 0.2s"
                          }}
                        >
                          <input
                            type="radio"
                            name={`box-${boxRate.box_number}`}
                            value={rate.object_id}
                            checked={selectedRates[boxRate.box_number] === rate.object_id}
                            onChange={() => setSelectedRates({
                              ...selectedRates,
                              [boxRate.box_number]: rate.object_id
                            })}
                            style={{ marginRight: "1rem", width: "18px", height: "18px" }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                              <Package size={16} style={{ color: "#64748b" }} />
                              <strong>{rate.provider}</strong>
                              <span style={{ color: "#64748b" }}>•</span>
                              <span style={{ color: "#64748b" }}>{rate.service}</span>
                            </div>
                            {rate.estimated_days && (
                              <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: "0.25rem" }}>
                                Estimated delivery: {rate.estimated_days} {rate.duration_terms || 'business days'}
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#1e293b" }}>
                            ${rate.amount}
                          </div>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div style={{ padding: "1rem", textAlign: "center", color: "#dc2626" }}>
                      No rates available for this box
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div style={{ 
              background: "#f0fdf4", 
              border: "2px solid #bbf7d0",
              padding: "1rem", 
              borderRadius: "8px", 
              marginBottom: "1.5rem" 
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: "0.875rem", color: "#064e3b", marginBottom: "0.25rem" }}>
                    Total Shipping Cost
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "#16a34a" }}>
                    {Object.keys(selectedRates).length} of {boxRates.length} boxes selected
                  </div>
                </div>
                <div style={{ fontSize: "2rem", fontWeight: 700, color: "#10b981" }}>
                  ${getTotalCost()}
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
              <button className="btn btn-outline" onClick={onClose}>
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleCreateLabels}
                disabled={Object.keys(selectedRates).length !== boxRates.length}
              >
                <Truck size={20} />
                Create Labels (${getTotalCost()})
              </button>
            </div>
          </div>
        )}

        {step === "creating" && (
          <div style={{ textAlign: "center", padding: "3rem" }}>
            <Loader2 size={48} style={{ color: "#3b82f6", animation: "spin 1s linear infinite", margin: "0 auto 1rem" }} />
            <p style={{ color: "#64748b" }}>Creating 4"×6" shipping labels...</p>
            <p style={{ fontSize: "0.875rem", color: "#94a3b8", marginTop: "0.5rem" }}>
              This may take a few moments
            </p>
          </div>
        )}

        {step === "results" && result && (
          <div>
            <div style={{ 
              background: result.summary.purchased > 0 ? "#f0fdf4" : "#fef2f2",
              border: `2px solid ${result.summary.purchased > 0 ? "#bbf7d0" : "#fecaca"}`,
              padding: "1rem", 
              borderRadius: "8px", 
              marginBottom: "1.5rem",
              display: "flex",
              alignItems: "center",
              gap: "0.75rem"
            }}>
              {result.summary.purchased > 0 ? (
                <>
                  <CheckCircle size={24} style={{ color: "#16a34a" }} />
                  <div>
                    <strong style={{ color: "#16a34a" }}>Success!</strong>
                    <p style={{ fontSize: "0.875rem", color: "#166534", marginTop: "0.25rem" }}>
                      Created {result.summary.purchased} shipping label{result.summary.purchased !== 1 ? 's' : ''} (4"×6" format)
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <AlertCircle size={24} style={{ color: "#dc2626" }} />
                  <div>
                    <strong style={{ color: "#dc2626" }}>Failed</strong>
                    <p style={{ fontSize: "0.875rem", color: "#991b1b", marginTop: "0.25rem" }}>
                      Could not create any labels
                    </p>
                  </div>
                </>
              )}
            </div>

            <div style={{ 
              background: "#f8fafc", 
              padding: "1rem", 
              borderRadius: "8px", 
              marginBottom: "1.5rem" 
            }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", fontSize: "0.875rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#64748b" }}>Total Boxes:</span>
                  <strong>{result.summary.total}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#64748b" }}>Labels Created:</span>
                  <strong style={{ color: "#16a34a" }}>{result.summary.purchased}</strong>
                </div>
                {result.summary.failed > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "#64748b" }}>Failed:</span>
                    <strong style={{ color: "#dc2626" }}>{result.summary.failed}</strong>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "0.5rem", borderTop: "1px solid #e2e8f0" }}>
                  <span style={{ color: "#64748b" }}>Total Cost:</span>
                  <strong style={{ fontSize: "1rem", color: "#10b981" }}>${result.summary.total_cost}</strong>
                </div>
              </div>
            </div>

            {result.created_shipments && result.created_shipments.length > 0 && (
              <div style={{ marginBottom: "1.5rem" }}>
                <h4 style={{ fontSize: "1rem", fontWeight: 600, color: "#1e293b", marginBottom: "0.75rem" }}>
                  Created Labels (4"×6" format)
                </h4>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  {result.created_shipments.map((shipment) => (
                    <div 
                      key={shipment.box_number}
                      style={{
                        background: "white",
                        border: "1px solid #e2e8f0",
                        padding: "1rem",
                        borderRadius: "8px"
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                        <strong style={{ color: "#1e293b" }}>Box {shipment.box_number}</strong>
                        <span style={{ color: "#10b981", fontWeight: 600 }}>${shipment.cost}</span>
                      </div>
                      <div style={{ fontSize: "0.875rem", color: "#64748b", marginBottom: "0.5rem" }}>
                        {shipment.carrier} - {shipment.service}
                      </div>
                      <div style={{ fontSize: "0.875rem", color: "#64748b", marginBottom: "0.75rem" }}>
                        Tracking: {shipment.tracking_number}
                      </div>
                      <a
                        href={shipment.label_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline"
                        style={{ width: "100%" }}
                      >
                        <ExternalLink size={16} />
                        View 4×6 Label
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.errors && result.errors.length > 0 && (
              <div style={{ marginBottom: "1.5rem" }}>
                <h4 style={{ fontSize: "1rem", fontWeight: 600, color: "#dc2626", marginBottom: "0.75rem" }}>
                  Errors
                </h4>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  {result.errors.map((err, idx) => (
                    <div 
                      key={idx}
                      style={{
                        background: "#fef2f2",
                        border: "1px solid #fecaca",
                        padding: "0.75rem",
                        borderRadius: "8px",
                        fontSize: "0.875rem"
                      }}
                    >
                      <strong>Box {err.box_number}:</strong> {err.error}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
              <button 
                className="btn btn-primary" 
                onClick={() => {
                  onClose();
                  navigate('/Shipments', { state: { selectedOrderNumber: mailing.order_number } });
                }}
              >
                <Truck size={20} />
                Done
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}